import { formatDurationEstimate } from './processing-status-badge';
import { Interval } from '../../../../backend/src/types/types';
import { formatRunCredits } from '@/helpers/runs';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';

interface RunApprovalPopoverContentProps {
  credits: number | null;
  pagesPerCredit: number;
  durationEstimate?: Interval | null;
  isSubmittingApproval: boolean;
  pendingAction?: 'approve' | 'deny' | null;
  onApprove: () => void;
  onDeny: () => void;
  runType?: string;
  subsetName?: string;
  requiresAdminApproval?: boolean;
  canApprove?: boolean;
}

export function RunApprovalPopoverContent({
  credits,
  pagesPerCredit,
  durationEstimate,
  isSubmittingApproval,
  pendingAction = null,
  onApprove,
  onDeny,
  runType,
  subsetName,
  requiresAdminApproval = false,
  canApprove = true,
}: RunApprovalPopoverContentProps) {
  const approvalTitle = requiresAdminApproval
    ? `Admin Approval Required${runType ? `: ${runType}` : ''}`
    : `Run Approval Required${runType ? `: ${runType}` : ''}`;

  const approvalSubtitle = subsetName
    ? `Recontextualising facts for ${subsetName}`
    : requiresAdminApproval
      ? 'This run exceeds the admin approval threshold and requires approval from an admin'
      : 'Review the details before proceeding';

  return (
    <>
      <div className="space-y-0.5 border-b px-4 py-3">
        <h4 className="font-medium text-gray-800">{approvalTitle}</h4>
        <p className="text-xs text-gray-500">{approvalSubtitle}</p>
      </div>

      <div className="p-1 text-xs">
        <ul className="mt-1.5 pl-2 text-gray-500 marker:text-gray-400">
          <li className="pl-1">
            Estimated credits:{' '}
            <strong className="text-gray-700">{credits === null ? 'Unavailable' : formatRunCredits(credits)}</strong>
          </li>

          {pagesPerCredit > 0 && (
            <li className="pl-1">
              Current plan: {pagesPerCredit.toLocaleString('en-GB')} {pagesPerCredit === 1 ? 'page' : 'pages'} per
              credit
            </li>
          )}

          {durationEstimate && (
            <li className="pl-1">
              Estimated time: <strong className="text-gray-700">{formatDurationEstimate(durationEstimate)}</strong>
            </li>
          )}
        </ul>

        <div className="flex flex-col gap-2 pt-3">
          {!canApprove && (
            <p className="mb-3 text-center text-xs text-amber-600">
              <Icon className="lucide--info mr-1.5 -mb-0.5 inline-block" />
              You don't have permission to approve this run. Ask an administrator to do this for you.
            </p>
          )}

          {isSubmittingApproval ? (
            <div className="mb-3 flex items-center justify-center gap-2 pt-1 text-blue-500">
              <Icon className="lucide--loader animate-spin ease-in-out" />
              <p className="text-xs">{pendingAction === 'deny' ? 'Cancelling run...' : 'Submitting approval...'}</p>
            </div>
          ) : (
            <div className="flex gap-2 p-1 pt-0">
              <Button
                variant="default"
                className="flex-1"
                onClick={onDeny}
                disabled={isSubmittingApproval || !canApprove}
              >
                <Icon className="lucide--x" />
                Cancel
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                onClick={onApprove}
                disabled={isSubmittingApproval || !canApprove}
              >
                <Icon className="lucide--check" />
                Approve
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
